import type React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from 'app/shared/core/Button';
import IconContact from 'images/icons/contact-white.svg';
import { listingUtils_userRecentlyContactedListing } from 'app/shared/utils/listingUtils';
import { useYieldCallback } from '@zillow/yield-callback';
import { css } from '@/styled-system/css';
import type { ListingDetails } from 'app/types/listingDetails.type';
import { useModalOpener } from 'app/shared/contexts/ModalContext/useModal';
import { emitTrackRequestContactStartEvent } from 'app/shared/models/Clickstream/ContactClickstreamEvents';
import { getGlobalLogger } from '@zg-rentals/logger-base';
import RouteActions from 'app/shared/flux/actions/RouteActions';
import ListingEngineActions from 'app/shared/flux/actions/ListingEngineActions';
import perfUtils from 'app/shared/utils/perfUtils';
import type { RootReduxState } from 'app/shared/flux/reducers';
import { saveSrpScrollPosition } from 'app/shared/hooks/useSrpScrollRestore';

const logger = getGlobalLogger();

interface ListingCardContactButtonProps {
  listing: ListingDetails;
  onClick?: () => void;
  isInButtonContainer?: boolean;
}

const getListingCardContactButtonStyles = (shouldShowButton: boolean, isInButtonContainer: boolean) =>
  css({
    flex: isInButtonContainer ? '1' : undefined,
    width: isInButtonContainer ? undefined : '100%',
    paddingTop: '0',
    paddingRight: isInButtonContainer ? '0' : '16px',
    paddingBottom: isInButtonContainer ? '0' : shouldShowButton ? '8px' : '0',
    paddingLeft: isInButtonContainer ? '0' : '16px',
  });

const ListingCardContactButton: React.FC<ListingCardContactButtonProps> = ({
  listing,
  onClick,
  isInButtonContainer = false,
}) => {
  const { openListingCardContactModal } = useModalOpener();
  const dispatch = useDispatch();
  const filter = useSelector((state: RootReduxState) => state.filter);
  const inquiredListings = useSelector((state: RootReduxState) => state.listings.listingGroups.inquired);

  const isBuilding = listing.building === true;

  const onClickCallback = useYieldCallback(() => {
    if (isBuilding) {
      perfUtils.setMarker('ListingCardV3Click');
      saveSrpScrollPosition({ type: 'list', maloneLotId: listing.maloneLotIdEncoded });
      dispatch(ListingEngineActions.setPreviewListing(listing));
      dispatch(ListingEngineActions.setActiveMarkerMaloneLotId(listing.maloneLotIdEncoded));
      if (listing?.uriV2) {
        dispatch(RouteActions.transitionToListing({ listingUri: listing.uriV2, keepMapLocation: false, filter }));
      }
      onClick?.();
      return;
    }

    try {
      emitTrackRequestContactStartEvent({
        listing,
        triggerLocation: 'search_results',
        triggerObject: 'property_card',
      });
      openListingCardContactModal({
        props: {
          listing,
        },
      });
    } catch (error) {
      logger?.error(error, 'Error opening SRP contact modal');
    }

    onClick?.();
  });

  const recentlyContacted =
    listing.userItemTypes && listing.userItemDates
      ? listingUtils_userRecentlyContactedListing(
          listing.userItemTypes as unknown as Array<never>,
          listing.userItemDates as unknown as Array<never>,
        )
      : false;

  const hasBeenInquired = inquiredListings.some(
    (inquiredListing) => inquiredListing.aliasEncoded === listing.aliasEncoded,
  );

  const labelMap = {
    buttonLabel: 'Contact',
    gaLabel: 'defaultCTA',
  };

  if (isBuilding) {
    labelMap.buttonLabel = 'View Units';
    labelMap.gaLabel = 'viewUnitsCTA';
  } else if (listing.waitlisted) {
    labelMap.buttonLabel = 'Add me to the waitlist';
    labelMap.gaLabel = 'waitlistedCTA';
  } else if (listing.popularity && listing.popularity.summary?.inquiries === 0) {
    labelMap.buttonLabel = 'Be the first to contact!';
    labelMap.gaLabel = 'firstToContactCTA';
  }

  if ((recentlyContacted || hasBeenInquired) && !isBuilding) {
    labelMap.buttonLabel = 'Contact again?';
    labelMap.gaLabel = 'contactAgainCTA';
  }

  const shouldShowButton = !!(listing && listing.active !== false && typeof labelMap.buttonLabel === 'string');

  const listingCardContactButtonStyles = getListingCardContactButtonStyles(shouldShowButton, isInButtonContainer);

  if (!shouldShowButton) {
    return null;
  }

  return (
    <div className={listingCardContactButtonStyles} data-testid="listing-card-contact-button">
      <Button
        aria-label={labelMap.buttonLabel}
        className="ListingCardContactButton"
        data-contact-label={labelMap.gaLabel}
        data-testid="listing-card-contact-button-btn"
        btnType={isBuilding ? 'primary-outline' : 'primary'}
        full
        bold
        size={'md'}
        icon={!isBuilding && IconContact ? { src: IconContact, width: '18px', height: '16px', alt: '' } : undefined}
        onClick={onClickCallback}
      >
        {labelMap.buttonLabel}
      </Button>
    </div>
  );
};

export default ListingCardContactButton;
